import styled from 'styled-components';
import theme, { textStyles as ts } from '../../theme';

const outcomeColors = {
  accepted: '#65D675',
  declined: '#C2E5FF',
  expired: '#F14722',
  'already-responded': '#FFEBBD',
  unavailable: '#F14722',
};

export const Content = styled.main`
  padding-top: ${theme('spacing.large')};
  line-height: 1.5;
  min-width: 0;
  overflow-wrap: break-word;

  @media (max-width: ${theme('layout.narrowWidth')}) {
    padding-top: ${theme('spacing.regular')};
  }

  button:focus-visible {
    outline: 3px solid ${theme('colors.accentSecondary')};
    outline-offset: 4px;
  }

  input:focus-visible {
    outline: 2px solid ${theme('colors.accentSecondary')};
    outline-offset: 2px;
  }

  input[aria-invalid='true'] {
    border: 1px solid ${theme('colors.destructive')};
  }
`;

export const Illustration = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 144px;
  background: ${theme('colors.backgroundVeryDark')};
  border-radius: ${theme('radius.regular')};
  gap: ${theme('spacing.large')};

  svg {
    width: 48px;
    height: 48px;
  }

  .fill {
    fill: ${theme('colors.accentPrimary')};
  }

  @media (max-width: ${theme('layout.narrowWidth')}) {
    min-height: 112px;
    gap: ${theme('spacing.regular')};

    svg {
      width: 36px;
      height: 36px;
    }
  }
`;

export const Introduction = styled.div`
  > div {
    padding-top: ${theme('spacing.veryLarge')};
  }

  h1 {
    padding-bottom: ${theme('spacing.regular')};
    line-height: 1.2;
  }
`;

export const Section = styled.section`
  margin-top: ${theme('spacing.veryLarge')};
`;

export const SectionTitle = styled.h2`
  ${ts.title};
  margin-bottom: ${theme('spacing.regular')};
`;

export const Medicines = styled.ul`
  padding: 0;
  list-style: none;
`;

export const Medicine = styled.li`
  position: relative;
  min-width: 0;
  overflow-wrap: anywhere;

  > div > span {
    min-width: 0;
  }

  & + &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${theme('spacing.large')};
    right: ${theme('spacing.large')};
    border-top: 1px solid ${theme('colors.lightgrey')};
  }
`;

export const Copy = styled.p`
  ${ts.body};
  line-height: 1.6;
`;

export const Hint = styled.p`
  ${ts.bodyPlaceholder};
  margin-top: ${theme('spacing.small')};
  line-height: 1.5;
`;

export const SupportForm = styled.form`
  margin-top: ${theme('spacing.large')};
`;

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme('spacing.regular')};
  margin-top: ${theme('spacing.large')};

  button {
    width: 100%;
    flex: auto;
  }

  @media (min-width: ${theme('layout.pageWidth')}) {
    flex-direction: row;

    button {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

const Feedback = styled.p`
  ${ts.bodyPlaceholder};
  margin-top: ${theme('spacing.regular')};
`;

export const ErrorMessage = styled(Feedback)`
  color: ${theme('colors.destructive')};
  min-height: 1.5em;
  line-height: 1.5;
`;

export const Confirmation = styled.div`
  padding-top: ${theme('spacing.huge')};
  text-align: left;

  > section {
    padding: 0 ${theme('spacing.large')};
  }

  @media (max-width: ${theme('layout.narrowWidth')}) {
    padding-top: ${theme('spacing.veryLarge')};

    > section {
      padding: 0 ${theme('spacing.medium')};
    }
  }
`;

export const ConfirmationIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  margin: 0 auto ${theme('spacing.large')};
  border-radius: 50%;
  background: ${({ outcome }) => outcomeColors[outcome]};
  color: ${theme('colors.textWhite')};

  svg {
    width: 56px;
    height: 56px;
  }
`;

export const ConfirmationTitle = styled.h1`
  ${ts.veryLargeTitle};
  line-height: 1.2;
  text-align: center;
  outline: none;

  @media (max-width: ${theme('layout.narrowWidth')}) {
    ${ts.largeTitle};
  }
`;

export const Callout = styled.div`
  padding: ${theme('spacing.large')};
  margin-top: ${theme('spacing.veryLarge')};
  border-radius: ${theme('radius.regular')};
  background: ${theme('colors.backgroundDark')};

  @media (max-width: ${theme('layout.narrowWidth')}) {
    padding: ${theme('spacing.medium')};
  }
`;

export const NextSteps = styled.ul`
  ${ts.body};
  padding-left: ${theme('spacing.large')};
  list-style: disc;
  line-height: 1.6;

  li + li {
    margin-top: ${theme('spacing.small')};
  }

  strong {
    font-weight: 700;
  }
`;

export const Footer = styled.footer`
  ${ts.bodySmall};
  border-top: 1px solid ${theme('colors.lightgrey')};
  margin-top: ${theme('spacing.huge')};
  padding-top: ${theme('spacing.large')};
  text-align: center;
`;
